<script setup lang="ts">
import { storeToRefs } from 'pinia'

import ActionButton from '~/components/shared/ActionButton.vue'

import { useGetServerSyncedTimestamp } from '~/composables/useGetServerSyncedTimestamp'

import { useUserStore } from '~/stores/user'
import { useUserInfluencerStore } from '~/stores/userInfluencer'

import pictureBuild from '~/helpers/images/picture_build'

import type ImageCropper from '~/components/images/cropper.vue'
import type { Influencer } from '~/types/influencer'

type Props = {
  influencer: Influencer
  userIsOwner?: boolean
}

const { influencer, userIsOwner = false } = defineProps<Props>()

const { id: USER_ID } = storeToRefs(useUserStore())
const { SET: SET_INFLUENCER } = useUserInfluencerStore()

const picture = ref<string | undefined>()
const cropper = ref<InstanceType<typeof ImageCropper> | null>(null)
const { timestamp } = useGetServerSyncedTimestamp('banner-image')

const bannerUrl = computed(() => {
  if (picture.value) {
    return picture.value
  } else {
    return (
      pictureBuild({
        kind: 'influencer',
        slug: influencer.slug,
        target: 'banner',
        size: '1280_290',
      }) + `?ts=${timestamp.value}`
    )
  }
})

defineExpose({
  addPicture,
  confirmCrop,
})

function addPicture() {
  cropper.value?.addPicture()
}

function confirmCrop() {
  if (!cropper.value) return
  picture.value = cropper.value.getCroppedCanvas()
  cropper.value.close()
  cropper.value
    .upload({
      id: USER_ID.value,
      slug: influencer.slug,
      kind: 'influencer',
    })
    .then((resp: { picture: string }) => {
      picture.value = resp.picture
      SET_INFLUENCER({ has_banner: true })
    })
    .catch((err: any) => {
      console.error(err)
    })
}
</script>

<template>
  <div class="profileBanner" data-test-id="influencerProfileBanner">
    <LazyImagesCropper
      v-if="userIsOwner"
      ref="cropper"
      target="banner"
      :aspect-ratio-x="128"
      :aspect-ratio-y="29"
      :modal-title="$t('influencer.profile.slug.edit.picture01')"
    >
      <template #actions>
        <div
          class="tw-mx-auto tw-my-6 tw-flex tw-items-center tw-justify-center tw-text-orange-500"
        >
          <div class="tw-mr-4 tw-cursor-pointer" @click="addPicture">
            {{ $t('shared.profile.banner00') }}
          </div>
          <ActionButton @click="confirmCrop">
            <span class="tw-font-medium">
              {{ $t('shared.profile.banner01') }}
            </span>
          </ActionButton>
        </div>
      </template>
    </LazyImagesCropper>
    <div v-if="influencer.has_banner || picture" class="tw-relative tw-h-full">
      <div class="tw-absolute tw-size-full tw-bg-[#000000] tw-bg-opacity-75" />
      <img
        class="tw-size-full tw-object-cover tw-object-center"
        :src="bannerUrl"
        alt=""
        fetchpriority="high"
      />
    </div>
  </div>
</template>
